/**
 * Protection Timeline Component
 *
 * Displays resource protection status, backup history, and at-risk indicators
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertTriangle, CheckCircle, Clock } from 'lucide-react'
import { calculateProtectionTimeline } from '@/services/analyticsService'
import type { Resource, Task } from '@/types'

interface ProtectionTimelineProps {
  resources: Resource[]
  tasks: Task[]
  isLoading?: boolean
}

export function ProtectionTimeline({
  resources,
  tasks,
  isLoading = false
}: ProtectionTimelineProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    )
  }

  const timeline = calculateProtectionTimeline(resources, tasks)
  const atRiskCount = timeline.filter((t: any) => t.status === 'at-risk').length
  const warningCount = timeline.filter((t: any) => t.status === 'warning').length
  const healthyCount = timeline.filter((t: any) => t.status === 'healthy').length

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600 dark:text-green-400'
      case 'warning': return 'text-yellow-600 dark:text-yellow-400'
      case 'at-risk': return 'text-red-600 dark:text-red-400'
      default: return 'text-muted-foreground'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-5 w-5 text-green-600" />
      case 'warning': return <Clock className="h-5 w-5 text-yellow-600" />
      case 'at-risk': return <AlertTriangle className="h-5 w-5 text-red-600" />
      default: return null
    }
  }

  const getStatusBadgeVariant = (status: string): 'default' | 'destructive' | 'secondary' => {
    switch (status) {
      case 'healthy': return 'default'
      case 'warning': return 'secondary'
      case 'at-risk': return 'destructive'
      default: return 'secondary'
    }
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Healthy Resources</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <span className="text-3xl font-bold">{healthyCount}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Regular backups with good success rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Warnings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="h-8 w-8 text-yellow-600" />
              <span className="text-3xl font-bold">{warningCount}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Backup frequency or success rate needs attention
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">At Risk</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-8 w-8 text-red-600" />
              <span className="text-3xl font-bold">{atRiskCount}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Requires immediate attention
            </p>
          </CardContent>
        </Card>
      </div>

      {/* At-Risk Alert */}
      {atRiskCount > 0 && (
        <Card className="border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <div>
                <p className="font-semibold text-red-700 dark:text-red-400">
                  {atRiskCount} {atRiskCount === 1 ? 'resource is' : 'resources are'} at risk
                </p>
                <p className="text-sm text-muted-foreground">
                  These resources have not been backed up recently or have high failure rates
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resource Timeline Cards */}
      <div>
        <h2 className="text-xl font-bold mb-4">Resource Protection Status</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {timeline.map((item: any) => {
            const successRate = item.totalBackups > 0
              ? ((item.successfulBackups / item.totalBackups) * 100).toFixed(1)
              : '0.0'

            return (
              <Card key={item.resourceId} className={
                item.status === 'at-risk'
                  ? 'border-red-200 dark:border-red-900'
                  : item.status === 'warning'
                  ? 'border-yellow-200 dark:border-yellow-900'
                  : ''
              }>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(item.status)}
                      <CardTitle className="text-base">{item.resourceName}</CardTitle>
                    </div>
                    <Badge variant={getStatusBadgeVariant(item.status)}>
                      {item.status.toUpperCase()}
                    </Badge>
                  </div>
                  {item.status !== 'healthy' && (
                    <CardDescription className={getStatusColor(item.status)}>
                      {item.reason}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Protected</p>
                      <p className="font-medium">{item.daysProtected} days</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Last backup</p>
                      <p className="font-medium">
                        {item.daysSinceLastBackup === 0
                          ? 'Today'
                          : `${item.daysSinceLastBackup} day${item.daysSinceLastBackup !== 1 ? 's' : ''} ago`}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Success rate</p>
                      <p className={`font-medium ${
                        parseFloat(successRate) >= 95
                          ? 'text-green-600'
                          : parseFloat(successRate) >= 85
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }`}>
                        {successRate}%
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Frequency</p>
                      <p className="font-medium">
                        Every {item.avgFrequencyDays.toFixed(1)} days
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Total backups</p>
                      <p className="font-medium">
                        {item.successfulBackups}/{item.totalBackups}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">First backup</p>
                      <p className="font-medium text-xs">
                        {new Date(item.firstBackupDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>

      {/* No Resources Message */}
      {timeline.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center p-8">
              <p className="text-muted-foreground">
                No resources found. Add resources to start tracking their protection status.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
